// src/components/ReportHeader/controls/index.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip
} from '@mui/material';
import {
  CalendarMonth,
  Settings,
  FileDownload,
  PictureAsPdf,
  TableChart
} from '@mui/icons-material';

import AreaSelectorControl from './AreaSelectorControl';
import CalendarButtonControl from './CalendarButtonControl';
import IconButtonControl from './IconButtonControl';



/**
 * Standard control styling for report headers
 * Ensures consistent appearance across all controls
 */
const getControlStyles = (variant = 'light') => {
  const styles = {
    light: {
      // Light controls on dark background (default)
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255,255,255,0.1)',
        color: 'white',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
      },
      '& .MuiInputBase-input': { color: 'white' },
      '& .MuiSvgIcon-root': { color: 'white' },
      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
    },
    yellow: {
      // Yellow controls (matching your existing pattern)
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#f1c40f',
        color: '#2c3e50',
        '& fieldset': { borderColor: '#f39c12' },
        '&:hover fieldset': { borderColor: '#f39c12' }
      },
      '& .MuiInputBase-input': { color: '#2c3e50' },
      '& .MuiSvgIcon-root': { color: '#2c3e50' }
    }
  };
  
  return styles[variant] || styles.light;
};

/**
 * Date Picker Control
 * Standard date input for reports
 */
export function DatePickerControl({ 
  value, 
  onChange, 
  label = '',
  variant = 'yellow',
  ...props 
}) {
  return (
    <TextField
      type="date"
      label={label}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: <CalendarMonth sx={{ mr: 1 }} />
      }}
      sx={getControlStyles(variant)}
      {...props}
    />
  );
}

/**
 * Area/Region Selector Control
 * Dropdown for selecting geographical areas
 */
// export function AreaSelectorControl({ 
//   value, 
//   onChange, 
//   options = [], 
//   label = 'Area',
//   variant = 'light',
//   showAll = true,
//   ...props 
// }) {
//   return (
//     <FormControl size="small" sx={{ minWidth: 120 }}>
//       <InputLabel sx={{ color: variant === 'light' ? 'rgba(255,255,255,0.7)' : '#2c3e50' }}>
//         {label}
//       </InputLabel>
//       <Select
//         value={value}
//         onChange={(e) => onChange?.(e.target.value)}
//         label={label}
//         sx={getControlStyles(variant)}
//         {...props}
//       >
//         {showAll && <MenuItem value="">All</MenuItem>}
//         {options.map((option) => (
//           <MenuItem key={option.value || option} value={option.value || option}>
//             {option.label || option}
//           </MenuItem>
//         ))}
//       </Select>
//     </FormControl>
//   );
// }

/**
 * Export Button Control
 * Standardized export functionality
 */
export function ExportButtonControl({ 
  type = 'csv', 
  onClick, 
  disabled = false,
  variant = 'yellow',
  ...props 
}) {
  const config = {
    csv: { icon: <TableChart />, text: 'CSV' },
    pdf: { icon: <PictureAsPdf />, text: 'PDF' },
    excel: { icon: <FileDownload />, text: 'Excel' }
  };

  const buttonConfig = config[type] || config.csv;
  
  const buttonStyles = {
    yellow: {
      backgroundColor: '#f1c40f',
      color: '#2c3e50',
      '&:hover': { backgroundColor: '#f39c12' }
    },
    light: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
    }
  };

  return (
    <Button
      variant="contained"
      size="small"
      startIcon={buttonConfig.icon}
      onClick={onClick}
      disabled={disabled}
      sx={buttonStyles[variant]}
      {...props}
    >
      {buttonConfig.text}
    </Button>
  );
}

/**
 * Settings Button Control
 * Opens report configuration/settings
 */
export function SettingsButtonControl({ 
  onClick, 
  disabled = false,
  variant = 'yellow',
  tooltip = 'Report Settings',
  ...props 
}) {
  const buttonStyles = {
    yellow: {
      backgroundColor: '#f1c40f',
      color: '#2c3e50',
      '&:hover': { backgroundColor: '#f39c12' }
    },
    light: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
    }
  };

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      size="small"
      sx={buttonStyles[variant]}
      {...props}
    >
      <Settings />
    </IconButton>
  );
}

/**
 * Status Chip Control
 * Shows report status or data indicators
 */
export function StatusChipControl({ 
  label, 
  status = 'info', 
  variant = 'light',
  ...props 
}) {
  const statusColors = {
    success: { backgroundColor: '#27ae60', color: 'white' },
    warning: { backgroundColor: '#f39c12', color: 'white' },
    error: { backgroundColor: '#e74c3c', color: 'white' },
    info: { backgroundColor: '#3498db', color: 'white' },
    default: { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }
  };

  return (
    <Chip
      label={label}
      size="small"
      sx={statusColors[status] || statusColors.default}
      {...props}
    />
  );
}

export { AreaSelectorControl };
export { IconButtonControl };
export { CalendarButtonControl };
// export { AreaSelectorControl as default } from './AreaSelectorControl';

// PropTypes for all controls
DatePickerControl.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['light', 'yellow'])
};

AreaSelectorControl.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['light', 'yellow']),
  showAll: PropTypes.bool
};

ExportButtonControl.propTypes = {
  type: PropTypes.oneOf(['csv', 'pdf', 'excel']),
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['light', 'yellow'])
};

SettingsButtonControl.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['light', 'yellow']),
  tooltip: PropTypes.string
};

StatusChipControl.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['success', 'warning', 'error', 'info', 'default']),
  variant: PropTypes.oneOf(['light', 'yellow'])
};